import { use, useContext, useEffect, useState } from "react";
import { useWarehouse } from "../Inventory/Hooks/useWarehouse";
import { STOCK_MODAL_MODES } from "../Inventory/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Trash2 } from "lucide-react";
import { EDIT, PROCUREMENT_MRR, VIEW } from "../../routes";
import {
  AccessDenied,
  GenericTable,
  RoleValidator,
  SearchableDropdown,
  ProductCategoryFilter,
  Spinner,
} from "../../Components/Common";
import { useProcurementMRR } from "./Hooks/useProcurementMRR";
import {
  getPMRRStatusColor,
  MATERIAL_COLUMNS_PMRR,
  MATERIAL_LABELS_PMRR,
  PMRR_STATUS,
} from "./constants";
import { toProperCase } from "../../Utils";
import { UserContext } from "../../Context";
import { getPriorityStatusColor, PRIORITY_OPTIONS, ROLES } from "../../Constants/Strings";
import {
  AdminApprovalButtons,
  BillingCompletionButtons,
  InchargeSubmitButtons,
  ProcurementActionButtons,
  SendForProcurementButton,
} from "./Components/SubmitButtonFlow.js";
import ProcurementMRRActions from "./Components/ProcurementMRRActions.js";

const ProcurementMRRCreate = () => {
  const [loading, setLoading] = useState(true);
  const {
    newProcurementMRR,
    fetchProcurementMRRById,
    updateProcurementMRRDetails,
  } = useProcurementMRR();
  const { fetchWarehouses } = useWarehouse();
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [formData, setFormData] = useState({
    notes: "",
    estimated_delivery_date: "",
    priority: "",
  });
  const location = useLocation();
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();
  const { user} = useContext(UserContext);
  const accessedRoles = [
    ROLES.INVENTORY_ADMIN,
    ROLES.INVENTORY_INCHARGE,
    ROLES.BILLING_INCHARGE,
    ROLES.PROCUREMENT_INCHARGE,
  ].includes(user?.role);
  const restrictCreatePage =
    user.role !== ROLES.INVENTORY_INCHARGE && mode === STOCK_MODAL_MODES.ADD;

  const isDisabled = mode !== STOCK_MODAL_MODES.ADD;

  const resetForm = () => {
    setFormData({ notes: "", estimated_delivery_date: "", priority: "" ,mrr_code:"", });
    setWarehouseId("");
    setWarehouse("");
    setMaterials([]);
    setStatus("");
  };

  const removeMaterial = (product) => {
    setMaterials((prev) => prev.filter((item) => item.id !== product.id));
    toast.success(`${product.product_name} removed form the list`);
  };

  const handleAddMaterial = (material) => {
    if (!material) return;
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) {
        toast.error(`${material.product_name} already added`);
        return prev;
      } else {
        toast.success(`${material.product_name} added`);
      }
      // skip adding duplicate
      return [...prev, { ...material, product: material.id, quantity: 1 }];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div
            className="flex flex-col"
            disabled={mode === STOCK_MODAL_MODES.VIEW}
          >
            <span className="font-semibold">{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM_name || row.UOM})
            </span>
          </div>
        );
      case "comments":
        return (
          <div className="border-b-2 border-gray-400">
            <input
              type="text"
              value={row.comments ?? ""}
              placeholder="Enter note..."
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, comments: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-32 h-7 bg-inherit border-none focus:ring-0  text-start"
            />
          </div>
        );
      case "quantity":
        return (
          <div className="border-b-2 border-gray-400 w-14">
            <input
              type="number"
              min="1"
              value={Number(row.quantity ?? 0)}
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, quantity: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-18 h-7 bg-inherit border-none focus:ring-0 text-start"
            />
          </div>
        );
      case "actions":
        return (
          <>
            <RoleValidator allowedRoles={ROLES.INVENTORY_INCHARGE}>
            <button
              className=" hover:underline ml-2"
              onClick={() => removeMaterial(row)}
            >
              <Trash2 className="h-5 w-5 text-red-400" />
            </button>
            </RoleValidator>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleSubmit = async (submitType) => {
    const missingFields = [];
    if (!warehouseId) missingFields.push("Requesting warehouse");
    if (!formData.estimated_delivery_date)
      missingFields.push("Expected delivery date");
    if (!formData.notes) missingFields.push("Notes");
    if (!formData.priority) missingFields.push("Priority");
    if (materials.length === 0) missingFields.push("at least one material");

    if (missingFields.length > 0) {
      toast.error(`Please provide: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      requesting_warehouse: warehouseId,
      status:submitType,
      notes: formData.notes,
      materials,
      estimated_delivery_date: formData.estimated_delivery_date,
      priority: formData.priority,
    };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await newProcurementMRR(payload);
    } else {
      data = await updateProcurementMRRDetails(param.instanceId, payload);
      setMaterials(data.materials);
      setFormData({
        notes: data.notes,
        estimated_delivery_date: data.estimated_delivery_date,
        priority: data.priority,
      });
      setWarehouse(data.requesting_warehouse_name);
      setWarehouseId(data.requesting_warehouse);
      setStatus(data.status);
    }
    navigate(PROCUREMENT_MRR.replace(":instanceId", data.id));
  };

  const getProcurementMRRData = async () => {
    setLoading(true);
    try {
      const data = await fetchProcurementMRRById(param.instanceId);

      if (data) {
        setFormData({
          notes: data.notes || "",
          estimated_delivery_date: data.estimated_delivery_date || "",
          priority: data.priority || "",
          mrr_code:data.mrr_code || "",
        });
        setWarehouseId(data.requesting_warehouse || "");
        setWarehouse(data.requesting_warehouse_name || "");
        setMaterials(data.materials || []);
        setStatus(data.status || "");
      } else {
        resetForm();
      }
    } catch (error) {
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (param.instanceId) {
      getProcurementMRRData();
      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] pt-10">
        <Spinner size={4} />
      </div>
    );

  if (!accessedRoles || restrictCreatePage) return <AccessDenied />;  

  return (
    <div className="min-h-[70vh]">
      <div className="flex justify-between items-center mt-2">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 mt-2 px-3 py-2">
          <button
            type="button"
            onClick={() => navigate(PROCUREMENT_MRR)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode === STOCK_MODAL_MODES.EDIT
            ? "Edit"
            : mode === STOCK_MODAL_MODES.VIEW
            ? ""
            : "Create"}{" "}
          Procurement Material Requisition Request
        </h2>
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-4 px-4 py-2">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white md:border-r-2 md:pr-8">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            PMRR Materials
          </h3>
          {mode === STOCK_MODAL_MODES.VIEW && (
            <div className="mb-3">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By (Warehouse)`}{" "}
                <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={
                  fetchWarehouses
                }
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
            </div>)}
          <div className="flex items-center justify-between gap-4 w-full">
            {mode !== STOCK_MODAL_MODES.VIEW && (
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By (Warehouse)`}{" "}
                <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={
                  fetchWarehouses
                }
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
            </div>)}
            {mode === STOCK_MODAL_MODES.VIEW && (
            <div className="w-[50%] mb-3">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                PMRR Code <sub className="text-red-500">*</sub>
              </label>
              <input
                type="text"
                value={formData.mrr_code}
                className={`w-full border border-gray-300 ${isDisabled ? "bg-gray-100" : ""
                  } text-gray-700 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none`}
                disabled={isDisabled}
              />
            </div>)}
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                Expected Delivery Date <sub className="text-red-500">*</sub>
              </label>
              <input
                type="date"
                value={formData.estimated_delivery_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    estimated_delivery_date: e.target.value,
                  }))
                }
                className={`w-full border border-gray-300 ${
                  isDisabled ? "bg-gray-100" : ""
                } text-gray-700 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none`}
                disabled={isDisabled}
              />
            </div>
          </div>
          {/* Notes Field */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
              Notes <sub className="text-red-500">*</sub>
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>
          <div className="flex flex-wrap md:flex-nowrap items-center md:items-end justify-between gap-3 md:gap-4">
            <div>
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                Priority <sub className="text-red-500">*</sub>
              </label>
              {mode === STOCK_MODAL_MODES.ADD ? (
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                  disabled={mode === STOCK_MODAL_MODES.VIEW}
                >
                  {" "}
                  <option>Select Priority</option>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}{" "}
                </select>
              ) : (
                <div>
                  <span
                    className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getPriorityStatusColor(
                      formData.priority
                    )}`}
                  >
                    {toProperCase(formData.priority || "")}
                  </span>
                </div>
              )}
            </div>
            {mode !== STOCK_MODAL_MODES.ADD && status && (
              <div>
                <label className="block text-gray-700  font-medium md:text-sm text-xs mb-1">
                  Status
                </label>
                <span
                  className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getPMRRStatusColor(
                    status
                  )}`}
                >
                  {toProperCase(status)}
                </span>
              </div>
            )}
            {/* Upload File Option for Procurement Incharge */}
            {mode === STOCK_MODAL_MODES.VIEW &&
              status === PMRR_STATUS.PENDING_PROCUREMENT &&
              user.role === ROLES.PROCUREMENT_INCHARGE && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowUploadPopup(true)}
                    className="bg-primary text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
                  >
                    Upload Files
                  </button>
                </div>
            )}

            {/* View files for all roles for pending billing and completed status*/}
            {mode === STOCK_MODAL_MODES.VIEW &&
              (status === PMRR_STATUS.PENDING_BILLING || status === PMRR_STATUS.COMPLETED) && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowUploadPopup(true)}
                    className="bg-primary text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition"
                  >
                    View Files
                  </button>
                </div>
            )}
          </div>
        </div>

        {/* Right Side — Selected Materials */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center text-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Selected Materials
          </h3>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <label className="block text-gray-700 font-medium text-xs md:text-sm mb-1">
                Add Product <sub className="text-red-500">*</sub>
              </label>
              <ProductCategoryFilter
                onProductSelect={handleAddMaterial}
              />
              <label className="block text-gray-700 font-medium text-xs md:text-sm my-2">
                Selected Product List
              </label>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg md:h-[50vh]">
              No materials selected yet.
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${
                mode === STOCK_MODAL_MODES.ADD ? "h-[26vh]" : "h-[23vh]"
              } md:h-[50vh] overflow-y-auto`}
            >
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMNS_PMRR.filter((col) => col !== "actions")
                      : MATERIAL_COLUMNS_PMRR
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_LABELS_PMRR}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}

          {/* Action Buttons Section */}
          <div className="mt-3">
            {((status === PMRR_STATUS.DRAFT &&
              mode === STOCK_MODAL_MODES.EDIT) ||
              mode === STOCK_MODAL_MODES.ADD) && (
              <InchargeSubmitButtons handleSubmit={handleSubmit} />
            )}

            {status === PMRR_STATUS.PENDING_APPROVAL &&
              mode === STOCK_MODAL_MODES.VIEW && (
                <AdminApprovalButtons handleSubmit={handleSubmit} />
              )}

            {status === PMRR_STATUS.APPROVED && (
              <SendForProcurementButton handleSubmit={handleSubmit} />
            )}

            {status === PMRR_STATUS.PENDING_PROCUREMENT &&
              mode === STOCK_MODAL_MODES.VIEW && (
                <ProcurementActionButtons handleSubmit={handleSubmit} />
              )}

            {status === PMRR_STATUS.PENDING_BILLING && (
              <BillingCompletionButtons handleSubmit={handleSubmit} />
            )}
          </div>

          {showUploadPopup && (
            <ProcurementMRRActions
              mrrId={param.instanceId}
              onClose={() => setShowUploadPopup(false)}
              status
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcurementMRRCreate;
